import { useParams } from 'react-router-dom';
import { Box, Button, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { centsToDollarAmount } from 'helpers';
import { useEstate_GetEstatesByPortalId_WithEstateWorkItemsQuery } from 'queries';

import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  menuHeader: {
    paddingLeft: 5,
    cursor: 'default',
  },
  description: {
    marginRight: '10px',
  },
  descriptionSmall: {
    marginRight: '10px',
    fontSize: '1em',
    width: '390px',
  },
  descriptionSmallEstate: {
    marginRight: '10px',
    fontSize: '1em',
    width: '270px',
  },
}));

export default function PortalBillingHistory() {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: estatesData, loading: estatesLoading } =
    useEstate_GetEstatesByPortalId_WithEstateWorkItemsQuery({
      variables: {
        input: { portalId },
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting estates by portal ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!estatesData || estatesLoading) {
    return <Spinner />;
  }

  const estateWorkItemColumns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'date',
      minWidth: 140,
      headerName: 'Date',
      renderCell: (params: GridRenderCellParams) => {
        const date = params.row.estateWorkItem.estatePayment.updatedAt.toString().slice(0, 10);
        return (
          <>
            <Tooltip title={date}>
              <Typography className={classes.description}>{date}</Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) =>
        params.row.estateWorkItem.estatePayment.updatedAt.toString(),
    },
    {
      disableColumnMenu: true,
      field: 'price',
      minWidth: 110,
      headerName: 'Price',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Tooltip
              title={`$${centsToDollarAmount(params.row.estateWorkItem.estatePayment.amountPaid)}`}
            >
              <Typography className={classes.description}>{`$${centsToDollarAmount(
                params.row.estateWorkItem.estatePayment.amountPaid,
              )}`}</Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) =>
        centsToDollarAmount(params.row.estateWorkItem.amountPaid),
    },
    {
      disableColumnMenu: true,
      field: 'taskPurchased',
      width: 390,
      headerName: 'Task Purchased',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <Tooltip title={params.row.estateWorkItem.workText}>
              <TextField
                className={classes.descriptionSmall}
                InputProps={{ disableUnderline: true }}
                value={params.row.estateWorkItem.workText}
                multiline
              />
            </Tooltip>
          </div>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.estateWorkItem.workText,
    },
    {
      disableColumnMenu: true,
      field: 'estate',
      width: 270,
      headerName: 'Estate',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <Tooltip title={params.row.estate.estateName}>
              <TextField
                className={classes.descriptionSmallEstate}
                InputProps={{ disableUnderline: true }}
                value={params.row.estate.estateName}
                multiline
              />
            </Tooltip>
          </div>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.estate.estateName,
    },
    {
      disableColumnMenu: true,
      field: 'invoice',
      minWidth: 110,
      headerName: 'Invoice',
      renderCell: (params: GridRenderCellParams) => {
        return (
          (params.row.estateWorkItem.estatePayment.stripeReceiptUrl && (
            <>
              <Tooltip title="Download Invoice/Receipt">
                <Typography className={classes.description}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ marginTop: '3px', padding: '2px 10px' }}
                    onClick={() => {
                      window
                        .open(params.row.estateWorkItem.estatePayment.stripeReceiptUrl, '_blank')!
                        .focus();
                    }}
                  >
                    Download
                  </Button>
                </Typography>
              </Tooltip>
            </>
          )) || <Typography className={classes.description}>N/A</Typography>
        );
      },
      valueGetter: (params: GridValueGetterParams) =>
        params.row.estateWorkItem.stripeReceiptUrl || 'N/A',
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const estateWorkItemRows: any[] = [];
  const estates = estatesData
    ? estatesData.data.map((estate) => {
        return {
          ...estate,
          estateWorkItems: estate.estateWorkItems?.filter(
            (estateWorkItem) => estateWorkItem.estatePayment,
          ),
        };
      })
    : [];
  for (const estate of estates) {
    for (const estateWorkItem of estate.estateWorkItems!) {
      estateWorkItemRows.push({ id: estateWorkItem.id, estate, estateWorkItem });
    }
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item md={12} xs={12}>
          <div style={{ width: '100%' }}>
            <DataGrid
              className={classes.root}
              columns={estateWorkItemColumns}
              rows={estateWorkItemRows}
              disableColumnReorder
              disableRowSelectionOnClick
              autoHeight={true}
              rowHeight={65}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              components={{
                Toolbar: () => (
                  <Box className={classes.menuHeader}>
                    <Typography variant="h5">Billing History</Typography>
                    <hr />
                  </Box>
                ),
              }}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}
